import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useLanguage, useT } from "@/lib/LanguageContext";
import useAdminLoad from "../../../../hooks/useAdminLoad";
import * as crmApi from "../../api/crmApi";
import { crmErrorMessage } from "../../api/crmErrors";
import { fmtDate, fmtDayMonth } from "../../format";
import CrmSelect from "../../ui/CrmSelect";
import CrmTextarea from "../../ui/CrmTextarea";
import CrmButton from "../../ui/CrmButton";
import EmptyState from "../../ui/EmptyState";
import CrmLoadFailure from "../../ui/CrmLoadFailure";
import { CRM } from "../../../../constants/testIds/crm";

/**
 * Session notes (OWNER ONLY; the tab is not offered to a helper and the server refuses
 * the read): intake responses read-only at the top, then a note editor attached to one
 * booking — pick the session, write, Save. Private to the owner; encrypted at rest;
 * never in exports.
 *
 * `?booking=<id>` (the Today card's deep link) preselects that session.
 */
export default function SessionNotesTab({ client }) {
  const t = useT("crm");
  const tc = useT("common");
  const { lang } = useLanguage();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [sessions, setSessions] = useState([]);
  const load = useCallback(async () => {
    const [notes, list] = await Promise.all([
      crmApi.getSessionNotes(client.id),
      crmApi.listSessions(client.id, { page: 1, pageSize: 200 }),
    ]);
    setData(notes);
    setSessions((list?.rows || []).filter((s) => s.status !== "cancelled"));
  }, [client.id]);
  const { loading, loadError, reload } = useAdminLoad(load);
  const [bookingId, setBookingId] = useState(params.get("booking") || "");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const notes = data?.notes || [];
  const noteFor = (id) => notes.find((n) => n.booking_id === id);
  // Default: the deep-linked booking, else the latest session.
  useEffect(() => {
    if (!bookingId && sessions.length) setBookingId(sessions[0].booking_id);
  }, [sessions, bookingId]);
  useEffect(() => { setText(noteFor(bookingId)?.body || ""); }, [bookingId, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true);
    try {
      const saved = await crmApi.saveSessionNote(client.id, bookingId, text.trim());
      setData((d) => ({ ...d, notes: [...(d?.notes || []).filter((n) => n.booking_id !== bookingId), { ...noteFor(bookingId), ...saved, booking_id: bookingId }] }));
      toast.success(t("notes.sessionSaved"));
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };

  if (loadError) return <CrmLoadFailure error={loadError} onRetry={reload} />;
  if (loading && !data) return <div className="h-32 animate-pulse rounded-2xl bg-eh-cream" aria-busy="true" />;

  const current = noteFor(bookingId);
  return (
    <div data-testid={CRM.clientNoteEditor} className="space-y-3 rounded-2xl bg-white p-4">
      <h3 className="font-display text-xl text-eh-ink">{t("notes.sessionTitle")}</h3>
      <div data-testid={CRM.clientIntake} className="rounded-2xl bg-eh-sage/30 px-4 py-3 text-sm text-eh-ink">
        <p className="font-semibold">{t("notes.intake")}</p>
        {(data?.intake || []).length === 0 ? <p className="mt-1">{t("notes.intakeEmpty")}</p> : (
          (data.intake).map((form, i) => (
            <dl key={`${form.modality_slug}-${i}`} className="mt-2 space-y-1">
              {(form.answers || []).map((a, j) => (
                <div key={j}><dt className="inline font-medium">{a.label}: </dt><dd className="inline">{a.value}</dd></div>
              ))}
              <p className="text-xs">{fmtDate(form.submitted_at, lang)}</p>
            </dl>
          ))
        )}
      </div>
      {sessions.length === 0 ? <EmptyState title={t("notes.noSessions")} /> : (
        <>
          <CrmSelect className="w-full sm:w-auto" value={bookingId} onChange={(e) => setBookingId(e.target.value)} aria-label={t("notes.pickSession")}
            options={sessions.map((s) => ({ value: s.booking_id, label: t("notes.sessionOption", { date: fmtDayMonth(s.slot_start, lang), service: s.service_name }) }))}
            data-testid={CRM.clientNoteSession} />
          <CrmTextarea rows={5} value={text} onChange={(e) => setText(e.target.value)} aria-label={t("notes.sessionTitle")}
            placeholder={t("notes.sessionPlaceholder")} maxLength={10000} />
          {current?.updated_at && <p className="text-xs text-eh-muted">{t("notes.lastSaved", { date: fmtDate(current.updated_at, lang), who: current.author_email || "" })}</p>}
          <CrmButton onClick={save} disabled={busy || !text.trim() || text.trim() === (current?.body || "")} data-testid={CRM.clientNoteSave}>{t("common.save")}</CrmButton>
        </>
      )}
    </div>
  );
}
